import { debounce } from 'lodash';
import { IDhlDataForValidation } from 'pages/Checkout/CheckoutPackstation/types';
import { useTranslations } from 'modules/i18n/useTranslations';
import { validateDhlPostNumber } from 'commerce/services/addresses';

const validatePostNumber = async ({
  firstName,
  lastName,
  postalCode,
}: IDhlDataForValidation): Promise<boolean> => {
  const { response, error } = await validateDhlPostNumber({
    firstname: firstName,
    lastname: lastName,
    postnumber: postalCode,
  });

  return error ? false : response?.data.isValid;
};

/** wrapper for debounce to return correct value from async debounce function
 *  got this function from lodash github https://github.com/lodash/lodash/issues/4815
 */
function asyncDebounce<F extends (...args: any[]) => Promise<any>>(
  func: F,
  wait?: number,
) {
  const resolveSet = new Set<(p: any) => void>();
  const rejectSet = new Set<(p: any) => void>();

  const debounced = debounce((args: Parameters<F>) => {
    func(...args)
      .then((...res) => {
        resolveSet.forEach((resolve) => resolve(...res));
        resolveSet.clear();
      })
      .catch((...res) => {
        rejectSet.forEach((reject) => reject(...res));
        rejectSet.clear();
      });
  }, wait);

  return (...args: Parameters<F>): ReturnType<F> =>
    new Promise((resolve, reject) => {
      resolveSet.add(resolve);
      rejectSet.add(reject);
      debounced(args);
    }) as ReturnType<F>;
}

export const debouncedDhlValidation = asyncDebounce(validatePostNumber, 1000);

export const getLoginValidationErrorMessages = (
  validationErrors: Record<string, any>,
  t: ReturnType<typeof useTranslations>,
) => {
  const errorMessages = Object.values(validationErrors)
    .map((error) => error?.message)
    .filter(Boolean)
    .join(', ');

  return errorMessages || t('common.errors.general.required');
};
